import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  MapPin, Mail, Phone, Globe, Instagram, Play, 
  BadgeCheck, Star, Sparkles, Calendar, ExternalLink,
  MessageSquare, Share2, Heart, ChevronLeft, Send, User
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BannerDisplay from "../components/BannerDisplay";
import { toast } from "sonner";
import { ProfileSEO } from "../components/SEO";
import NotFound from "./NotFound";
import axios from "axios";
import { API, useAuth } from "../App";
import { formatApiError } from "../lib/utils";

// Star Rating Component
const StarRating = ({ rating, setRating, readonly = false, size = "md" }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-6 w-6",
    lg: "h-8 w-8"
  };
  
  return (
    <div className="star-rating flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          className={`star transition-all duration-200 ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-125'}`}
          onMouseEnter={() => !readonly && setHoverRating(star)}
          onMouseLeave={() => !readonly && setHoverRating(0)}
          onClick={() => !readonly && setRating && setRating(star)}
        >
          <Star 
            className={`${sizeClasses[size]} ${
              (hoverRating || rating) >= star 
                ? 'fill-yellow-400 text-yellow-400 drop-shadow-[0_0_6px_rgba(250,204,21,0.5)]' 
                : 'text-zinc-600'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Reviews Section Component
const ReviewsSection = ({ profileId, profileUserId, avgRating, reviewCount }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newReview, setNewReview] = useState({
    rating: 0,
    title: "",
    content: ""
  });

  useEffect(() => {
    fetchReviews();
  }, [profileId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/profiles/${profileId}/reviews`);
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Please log in to leave a review");
      navigate("/login");
      return;
    }

    if (newReview.rating === 0) {
      toast.error("Please select a star rating");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/profiles/${profileId}/reviews`,
        newReview,
        { withCredentials: true }
      );
      toast.success("Review submitted successfully!");
      setNewReview({ rating: 0, title: "", content: "" });
      setShowForm(false);
      fetchReviews();
    } catch (error) {
      const message = formatApiError(error, "Failed to submit review");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const canReview = user && user.user_id !== profileUserId;

  return (
    <div className="space-y-6">
      {/* Rating Summary */}
      <div className="bg-zinc-900 border border-zinc-800 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="font-heading text-4xl text-white">
                {avgRating ? avgRating.toFixed(1) : "0.0"}
              </div>
              <StarRating rating={avgRating || 0} readonly size="sm" />
              <p className="text-zinc-500 text-sm mt-1">
                {reviewCount || 0} {reviewCount === 1 ? 'review' : 'reviews'}
              </p>
            </div>
          </div>
          
          {canReview && !showForm && (
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-orange-600 hover:bg-orange-500"
              data-testid="write-review-btn"
            >
              <Star className="h-4 w-4 mr-2" />
              Write a Review
            </Button>
          )}
        </div>
      </div>

      {/* Review Form */}
      {showForm && (
        <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="review-form">
          <h3 className="font-heading text-xl text-white mb-4">Write Your Review</h3>
          <form onSubmit={handleSubmitReview} className="space-y-4">
            <div>
              <Label className="text-zinc-300 mb-2 block">Your Rating *</Label>
              <StarRating 
                rating={newReview.rating} 
                setRating={(rating) => setNewReview({ ...newReview, rating })}
                size="lg"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Review Title</Label>
              <Input
                value={newReview.title}
                onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                placeholder="Sum up your experience"
                className="mt-2 bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Your Review</Label>
              <Textarea
                value={newReview.content}
                onChange={(e) => setNewReview({ ...newReview, content: e.target.value })}
                placeholder="Share details of your experience working with this creative..."
                className="mt-2 min-h-24 bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={submitting}
                className="bg-orange-600 hover:bg-orange-500"
                data-testid="submit-review-btn"
              >
                {submitting ? "Submitting..." : "Submit Review"}
                <Send className="h-4 w-4 ml-2" />
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setNewReview({ rating: 0, title: "", content: "" });
                }}
                className="border-zinc-700 text-zinc-300"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-zinc-900 border border-zinc-800 p-6 skeleton h-32" />
          ))}
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div 
              key={review.review_id} 
              className="review-card"
              data-testid={`review-${review.review_id}`}
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {review.reviewer_picture ? (
                    <img 
                      src={review.reviewer_picture} 
                      alt={review.reviewer_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <User className="h-6 w-6 text-zinc-500" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <div>
                      <h4 className="font-medium text-white">{review.reviewer_name}</h4>
                      <p className="text-xs text-zinc-500">{formatDate(review.created_at)}</p>
                    </div>
                    <StarRating rating={review.rating} readonly size="sm" />
                  </div>
                  
                  {review.title && (
                    <h5 className="font-medium text-zinc-200 mb-2">{review.title}</h5>
                  )}
                  
                  {review.content && (
                    <p className="text-zinc-400 text-sm whitespace-pre-wrap">{review.content}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800">
          <Star className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500 mb-2">No reviews yet</p>
          {canReview && (
            <p className="text-zinc-600 text-sm">Be the first to review this creative!</p>
          )}
        </div>
      )}
    </div>
  );
};

export default function ProfilePage() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageOpen, setMessageOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [profileId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profiles/${profileId}`);
      setProfile(response.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleContact = async (type) => {
    try {
      await axios.post(`${API}/profiles/${profileId}/contact`);
    } catch (error) {
      // Silent fail for analytics
    }

    if (type === "email" && profile.user_email) {
      window.location.href = `mailto:${profile.user_email}`;
    } else if (type === "whatsapp" && profile.whatsapp) {
      window.open(`https://wa.me/${profile.whatsapp.replace(/\D/g, "")}`, "_blank");
    } else if (type === "website" && profile.website) {
      window.open(profile.website, "_blank");
    }
  };

  const sendMessage = async () => {
    if (!user) {
      toast.error("Please log in to send messages");
      navigate("/login");
      return;
    }

    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSending(true);
    try {
      await axios.post(
        `${API}/messages`,
        {
          recipient_id: profile.user_id,
          content: message,
        },
        { withCredentials: true }
      );
      toast.success("Message sent!");
      setMessageOpen(false);
      setMessage("");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const getVideoEmbed = (url) => {
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    return url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return <NotFound />;
  }

  const images = profile.portfolio?.filter((p) => p.type === "image") || [];
  const videos = profile.portfolio?.filter((p) => p.type === "video") || [];

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <ProfileSEO profile={profile} />
      
      {/* Header with Cover Image */}
      <div className="relative h-64 md:h-96 overflow-hidden">
        <div className="absolute inset-0 bg-zinc-900">
          {images[0] ? (
            <img
              src={images[0].url}
              alt={profile.user_name}
              className="w-full h-full object-cover opacity-60"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900" />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
        
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 text-white bg-black/30 hover:bg-black/50"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back
        </Button>

        {/* Share Button */}
        <Button
          variant="ghost"
          onClick={() => {
            navigator.clipboard.writeText(window.location.href);
            toast.success("Link copied to clipboard");
          }}
          className="absolute top-4 right-4 text-white bg-black/30 hover:bg-black/50"
        >
          <Share2 className="h-5 w-5" />
        </Button>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 -mt-32 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Profile Info */}
          <div className="lg:col-span-1">
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              {/* Profile Picture */}
              <div className="flex flex-col items-center mb-6">
                <div className="w-32 h-32 rounded-full border-4 border-zinc-900 overflow-hidden mb-4 -mt-20 bg-zinc-800">
                  {profile.user_picture ? (
                    <img
                      src={profile.user_picture}
                      alt={profile.user_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-500 text-4xl font-heading">
                      {profile.user_name?.charAt(0)?.toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Name & Badges */}
                <h1 className="font-heading text-2xl text-white text-center">{profile.user_name}</h1>
                {profile.company_logo && (
                  <div className="mt-3 h-14 w-32 bg-white/95 flex items-center justify-center p-2" data-testid="profile-company-logo">
                    <img src={profile.company_logo} alt={`${profile.user_name} company logo`} className="max-h-full max-w-full object-contain" />
                  </div>
                )}
                
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  {profile.is_featured && (
                    <span className="badge-featured">
                      <Sparkles className="h-3 w-3" />
                      FEATURED
                    </span>
                  )}
                  {profile.is_verified && (
                    <span className="badge-verified">
                      <BadgeCheck className="h-3 w-3" />
                      VERIFIED
                    </span>
                  )}
                  {profile.is_premium && (
                    <span className="badge-premium">
                      <Star className="h-3 w-3" />
                      PRO
                    </span>
                  )}
                </div>
              </div>

              {/* Location */}
              {profile.location && (
                <div className="flex items-center gap-2 text-zinc-400 mb-4">
                  <MapPin className="h-4 w-4 text-orange-500" />
                  <span>{profile.location}</span>
                </div>
              )}

              {/* Tagline */}
              {profile.tagline && (
                <p className="text-zinc-400 mb-6">{profile.tagline}</p>
              )}

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 py-4 border-t border-b border-zinc-800 mb-6">
                <div className="text-center">
                  <div className="font-heading text-2xl text-white">{profile.view_count || 0}</div>
                  <div className="text-xs text-zinc-500 uppercase">Views</div>
                </div>
                <div className="text-center">
                  <div className="font-heading text-2xl text-white">{profile.contact_count || 0}</div>
                  <div className="text-xs text-zinc-500 uppercase">Contacts</div>
                </div>
              </div>

              {/* Rating Display */}
              {(profile.avg_rating > 0 || profile.review_count > 0) && (
                <div className="flex items-center gap-3 mb-6 p-3 bg-zinc-800/50 rounded">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        className={`h-4 w-4 ${
                          star <= Math.round(profile.avg_rating || 0)
                            ? 'fill-yellow-400 text-yellow-400' 
                            : 'text-zinc-600'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="font-medium text-white">{(profile.avg_rating || 0).toFixed(1)}</span>
                  <span className="text-zinc-500 text-sm">({profile.review_count || 0} reviews)</span>
                </div>
              )}

              {/* Day Rate */}
              {(profile.day_rate_min || profile.day_rate_max) && (
                <div className="mb-6">
                  <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-1">Day Rate</p>
                  <p className="text-xl font-heading text-orange-500">
                    {profile.day_rate_min && profile.day_rate_max
                      ? `R${profile.day_rate_min.toLocaleString()} - R${profile.day_rate_max.toLocaleString()}`
                      : profile.day_rate_min
                      ? `From R${profile.day_rate_min.toLocaleString()}`
                      : `Up to R${profile.day_rate_max.toLocaleString()}`}
                  </p>
                </div>
              )}

              {/* Experience & Availability */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {profile.experience_level && (
                  <div>
                    <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-1">Experience</p>
                    <p className="text-zinc-300 capitalize">{profile.experience_level}</p>
                  </div>
                )}
                {profile.availability && (
                  <div>
                    <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-1">Availability</p>
                    <p className={`capitalize ${
                      profile.availability === "available" 
                        ? "text-teal-400" 
                        : profile.availability === "busy"
                        ? "text-yellow-400"
                        : "text-red-400"
                    }`}>
                      {profile.availability}
                    </p>
                  </div>
                )}
              </div>

              {/* Contact Buttons */}
              <div className="space-y-2">
                {/* Messaging - Only for Pro/Premium profiles */}
                {profile.is_premium ? (
                  <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-orange-600 hover:bg-orange-500" data-testid="message-btn">
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Send Message
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-zinc-800">
                      <DialogHeader>
                        <DialogTitle>Message {profile.user_name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <Textarea
                          placeholder="Write your message..."
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          className="min-h-32 bg-zinc-800 border-zinc-700"
                        />
                        <Button
                          onClick={sendMessage}
                          disabled={sending}
                          className="w-full bg-orange-600 hover:bg-orange-500"
                        >
                          {sending ? "Sending..." : "Send Message"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <div className="p-3 bg-zinc-800/50 border border-zinc-700 text-center">
                    <p className="text-zinc-400 text-sm">Use contact details below to reach this creative</p>
                  </div>
                )}

                {/* Contact Details - Available for all tiers */}
                {profile.user_email && (
                  <Button
                    variant="outline"
                    className="w-full border-zinc-700 text-zinc-300"
                    onClick={() => handleContact("email")}
                    data-testid="email-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </Button>
                )}

                {profile.whatsapp && (
                  <Button
                    variant="outline"
                    className="w-full border-zinc-700 text-zinc-300"
                    onClick={() => handleContact("whatsapp")}
                    data-testid="whatsapp-btn"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    WhatsApp
                  </Button>
                )}

                {/* Website - Available for all tiers (backlink) */}
                {profile.website && (
                  <Button
                    variant="outline"
                    className="w-full border-zinc-700 text-zinc-300"
                    onClick={() => handleContact("website")}
                    data-testid="website-btn"
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    Visit Website
                  </Button>
                )}
              </div>

              {/* Social Links */}
              <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-zinc-800">
                {profile.website && (
                  <a
                    href={profile.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-500 hover:text-orange-500 transition-colors"
                  >
                    <Globe className="h-5 w-5" />
                  </a>
                )}
                {profile.instagram && (
                  <a
                    href={`https://instagram.com/${profile.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-500 hover:text-orange-500 transition-colors"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                )}
                {profile.vimeo && (
                  <a
                    href={profile.vimeo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-500 hover:text-orange-500 transition-colors"
                  >
                    <Play className="h-5 w-5" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Portfolio & Details */}
          <div className="lg:col-span-2">
            <BannerDisplay placement="profile" className="mb-6" />
            <Tabs defaultValue="portfolio" className="space-y-6">
              <TabsList className="bg-zinc-900 border border-zinc-800">
                <TabsTrigger value="portfolio" className="data-[state=active]:bg-orange-600">
                  Portfolio
                </TabsTrigger>
                <TabsTrigger value="about" className="data-[state=active]:bg-orange-600">
                  About
                </TabsTrigger>
                <TabsTrigger value="skills" className="data-[state=active]:bg-orange-600">
                  Skills & Services
                </TabsTrigger>
                <TabsTrigger value="reviews" className="data-[state=active]:bg-orange-600" data-testid="reviews-tab">
                  Reviews {profile.review_count > 0 && `(${profile.review_count})`}
                </TabsTrigger>
              </TabsList>

              {/* Portfolio Tab */}
              <TabsContent value="portfolio" className="space-y-8">
                {/* Videos */}
                {videos.length > 0 && (
                  <div>
                    <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">
                      Video Work
                    </h3>
                    <div className="grid gap-4">
                      {videos.map((video) => (
                        <div
                          key={video.item_id}
                          className="relative aspect-video bg-zinc-900 border border-zinc-800 overflow-hidden"
                        >
                          <iframe
                            src={getVideoEmbed(video.url)}
                            title={video.title || "Video"}
                            className="w-full h-full"
                            allowFullScreen
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Images */}
                {images.length > 0 && (
                  <div>
                    <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">
                      Photo Gallery
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {images.map((image) => (
                        <button
                          key={image.item_id}
                          onClick={() => setSelectedMedia(image)}
                          className="relative aspect-square overflow-hidden group"
                        >
                          <img
                            src={image.url}
                            alt={image.title || "Portfolio image"}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <ExternalLink className="h-6 w-6 text-white" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {videos.length === 0 && images.length === 0 && (
                  <div className="text-center py-16 bg-zinc-900 border border-zinc-800">
                    <Play className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500">No portfolio items yet</p>
                  </div>
                )}
              </TabsContent>

              {/* About Tab */}
              <TabsContent value="about" className="space-y-6">
                <div className="bg-zinc-900 border border-zinc-800 p-6">
                  <h3 className="font-heading text-xl text-white mb-4">About</h3>
                  {profile.bio ? (
                    <p className="text-zinc-400 whitespace-pre-wrap">{profile.bio}</p>
                  ) : (
                    <p className="text-zinc-600 italic">No bio provided yet</p>
                  )}
                </div>

                {profile.gear_owned?.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 p-6">
                    <h3 className="font-heading text-xl text-white mb-4">Equipment</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.gear_owned.map((gear, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="bg-transparent border-zinc-700 text-zinc-400"
                        >
                          {gear}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Skills Tab */}
              <TabsContent value="skills" className="space-y-6">
                {profile.skills?.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 p-6">
                    <h3 className="font-heading text-xl text-white mb-4">Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.skills.map((skill, index) => (
                        <Badge
                          key={index}
                          className="bg-orange-900/30 text-orange-400 border border-orange-800"
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {profile.services?.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 p-6">
                    <h3 className="font-heading text-xl text-white mb-4">Services</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.services.map((service, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="bg-transparent border-zinc-700 text-zinc-400"
                        >
                          {service}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Reviews Tab */}
              <TabsContent value="reviews" className="space-y-6" data-testid="reviews-section">
                <ReviewsSection 
                  profileId={profileId} 
                  profileUserId={profile.user_id}
                  avgRating={profile.avg_rating}
                  reviewCount={profile.review_count}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800 p-0">
          {selectedMedia && (
            <img
              src={selectedMedia.url}
              alt={selectedMedia.title || "Portfolio image"}
              className="w-full h-auto"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
